/**
 * ContactPage — PakPoints Store contact (/store/contact).
 * Submits to main app POST /api/inquiries/ (same as main site contact). Displays shop@pakpoints.com for store support.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageContainer from '../components/common/PageContainer';
import inquiryService from '../../services/inquiryService';

const STORE_SUPPORT_EMAIL = 'shop@pakpoints.com';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    contactSubject: '',
    contactMessage: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageIsError, setMessageIsError] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    setMessageIsError(false);

    const name = formData.customerName.trim();
    const email = formData.customerEmail.trim();
    const subjectText = (formData.contactSubject || '').trim() || 'PakPoints Store inquiry';
    const body = (formData.contactMessage || '').trim();

    if (!name || !email || !body) {
      setMessageIsError(true);
      setMessage('Please fill in your name, email, and message.');
      setSubmitting(false);
      return;
    }

    try {
      const subject = subjectText.length > 200 ? `${subjectText.slice(0, 197)}…` : subjectText;
      await inquiryService.createInquiry({
        category: 'general',
        name,
        email,
        phone: '',
        subject,
        message: `[Store /contact] ${body}`,
      });
      setMessage('Thank you for your message. We will get back to you soon!');
      setFormData({
        customerName: '',
        customerEmail: '',
        contactSubject: '',
        contactMessage: ''
      });
    } catch (error) {
      const data = error.response?.data;
      const errMsg =
        (data && data.message) ||
        (typeof data?.errors === 'object' && Object.values(data.errors).flat().join(' ')) ||
        error.message ||
        'Error sending message. Please try again.';
      setMessageIsError(true);
      setMessage(typeof errMsg === 'string' ? errMsg : 'Error sending message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      {/* Breadcrumb */}
      <div className="breadcrumb-area">
        <div className="container">
          <div className="breadcrumb-content">
            <ul>
              <li><Link to="/store">Home</Link></li>
              <li className="active">Contact</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Contact Main Page Area */}
      <div className="contact-main-page mt-60 mb-40 mb-md-40 mb-sm-40 mb-xs-40">
        <div className="container">
          <div className="row">
            {/* Contact Info */}
            <div className="col-lg-5 offset-lg-1 col-md-12 order-1 order-lg-2">
              <div className="contact-page-side-content">
                <h3 className="contact-page-title">Contact Us</h3>
                <p className="contact-page-message mb-25">
                  We're here to help! If you have any questions, concerns, or feedback, 
                  please don't hesitate to reach out to us. Our team is ready to assist you.
                </p>
                <div className="single-contact-block">
                  <h4><i className="fa fa-fax"></i> Address</h4>
                  <p>Commercial Market, Block D, AWT Phase 2, Lahore 54600, Pakistan</p>
                </div>
                <div className="single-contact-block">
                  <h4><i className="fa fa-phone"></i> Phone</h4>
                  <p>Mobile: <a href="tel:+923330490575">+92 333 0490575</a></p>
                  <p>Monday to Friday, 9 AM to 6 PM</p>
                </div>
                <div className="single-contact-block last-child">
                  <h4><i className="fa fa-envelope-o"></i> Email</h4>
                  <p><a href={`mailto:${STORE_SUPPORT_EMAIL}`}>{STORE_SUPPORT_EMAIL}</a></p>
                  <p>Store orders and support</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="col-lg-6 col-md-12 order-2 order-lg-1">
              <div className="contact-form-content pt-sm-55 pt-xs-55">
                <h3 className="contact-page-title">Tell Us Your Message</h3>
                <div className="contact-form">
                  <form id="contact-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label>Your Name <span className="required">*</span></label>
                      <input
                        type="text"
                        name="customerName"
                        id="customername"
                        value={formData.customerName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Your Email <span className="required">*</span></label>
                      <input
                        type="email"
                        name="customerEmail"
                        id="customerEmail"
                        value={formData.customerEmail}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Subject</label>
                      <input
                        type="text"
                        name="contactSubject"
                        id="contactSubject"
                        value={formData.contactSubject}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group mb-30">
                      <label>Your Message <span className="required">*</span></label>
                      <textarea
                        name="contactMessage"
                        id="contactMessage"
                        value={formData.contactMessage}
                        onChange={handleChange}
                        rows="5"
                        required
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <button
                        type="submit"
                        className="li-btn-3"
                        disabled={submitting}
                      >
                        {submitting ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </form>
                </div>
                {message && (
                  <p
                    className={`form-messege mt-3 ${messageIsError ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}
                    role="alert"
                  >
                    {message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default ContactPage;
